import { Routes, Route, BrowserRouter as Router, useLocation } from "react-router-dom";
import { routeNames } from "./consts/routesNames";
import PrivateRoutes from "./utils/PrivateRoutes";
import RequireAdmin from "./components/auth/RequireAdmin";
import RequirePermission from "./components/auth/RequirePermission";
import { PERMISSIONS } from "./auth/utils/permissionCodes";

// Components
import GenericModal from "./components/common/modal/GenericModal";
import ScrollToTop from "./components/common/scrollToTop/ScrollToTop";
import Navbar from "./components/navbar/Navbar";
import MobileNavbar from "./components/mobileNavbar/MobileNavbar";

// ---- Pages ---- //
// Common
import HomePage from "./pages/homepage/Homepage";
import NoPage from "./pages/common/nopage/NoPage";

// Authentication
import Register from "./pages/authentication/register/Register";
import Login from "./pages/authentication/login/Login";
import ForgotPasswordCheckEmail from "./pages/authentication/forgotPasswordCheckEmail/ForgotPasswordCheckEmail";
import ForgotPasswordReset from "./pages/authentication/forgotPasswordReset/ForgotPasswordReset";
import Logout from "./utils/Logout";

// Contacts
import DisplayContacts from "./pages/contacts/displayContacts/DisplayContacts";
import AddEditContacts from "./pages/contacts/addEditContacts/AddEditContacts";
import ContactDetails from "./pages/contacts/contactDetails/ContactDetails";

// Items
import DisplayItems from "./pages/items/displayItems/DisplayItems";
import AddEditItems from "./pages/items/addEditItems/AddEditItems";
import ItemDetails from "./pages/items/itemDetails/ItemDetails";

// Properties
import DisplayProperties from "./pages/properties/displayProperties/DisplayProperties";
import AddEditProperties from "./pages/properties/addEditProperties/AddEditProperties";
import PropertyDetails from "./pages/properties/propertyDetails/PropertyDetails";

// Companies
import DisplayCompanies from "./pages/companies/displayCompanies/DisplayCompanies";
import CompanyDetails from "./pages/companies/companyDetails/CompanyDetails";
import AddEditCompanies from "./pages/companies/addEditCompanies/AddEditCompanies";

// Lists
import DisplayLists from "./pages/lists/displayLists/DisplayLists";

import useWindowSize from "./hooks/useWindowSize";

/**
 * Navbar-only resize subscription so window width updates do not re-render the entire <Routes> tree.
 */
function NavbarSwitcher() {
  const { windowWidth } = useWindowSize();
  const location = useLocation();
  const authRoutes = [routeNames.LOGIN, routeNames.FORGOT_PASSWORD_EMAIL, routeNames.FORGOT_PASSWORD_RESET];
  const shouldShowNavbar = !authRoutes.includes(location.pathname);

  if (!shouldShowNavbar) return null;
  return windowWidth > 1260 ? <Navbar /> : <MobileNavbar />;
}

function RouteContent() {
  return (
    <>
      <ScrollToTop />
      <GenericModal />
      <NavbarSwitcher />
      <Routes>
        {/* Public Routes */}
        <Route path={routeNames.LOGIN} element={<Login />} />
        <Route path={routeNames.FORGOT_PASSWORD_EMAIL} element={<ForgotPasswordCheckEmail />} />
        <Route path={routeNames.FORGOT_PASSWORD_RESET} element={<ForgotPasswordReset />} />
        <Route path={routeNames.LOGOUT} element={<Logout />} />
        <Route path="*" element={<NoPage />} />
        {/* Private Routes */}
        <Route element={<PrivateRoutes />}>
          <Route path="/" element={<HomePage />} />
          <Route element={<RequireAdmin />}>
            <Route path={routeNames.REGISTER} element={<Register />} />
            <Route path={routeNames.LISTS} element={<DisplayLists />} />
          </Route>
          {/* Contact Routes */}
          <Route element={<RequirePermission permission={PERMISSIONS.CONTACTS.VIEW} />}>
            <Route path={routeNames.CONTACTS} element={<DisplayContacts />} />
            <Route path={routeNames.CONTACT_DETAILS} element={<ContactDetails />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.CONTACTS.CREATE} />}>
            <Route path={routeNames.ADD_CONTACT} element={<AddEditContacts />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.CONTACTS.UPDATE} />}>
            <Route path={routeNames.EDIT_CONTACT} element={<AddEditContacts />} />
          </Route>
          {/* Items Routes */}
          <Route element={<RequirePermission permission={PERMISSIONS.ITEMS.VIEW} />}>
            <Route path={routeNames.ITEMS} element={<DisplayItems />} />
            <Route path={routeNames.ITEM_DETAILS} element={<ItemDetails />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.ITEMS.CREATE} />}>
            <Route path={routeNames.ADD_ITEM} element={<AddEditItems />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.ITEMS.UPDATE} />}>
            <Route path={routeNames.EDIT_ITEM} element={<AddEditItems />} />
          </Route>
          {/* Properties Routes */}
          <Route element={<RequirePermission permission={PERMISSIONS.PROPERTIES.VIEW} />}>
            <Route path={routeNames.PROPERTIES} element={<DisplayProperties />} />
            <Route path={routeNames.PROPERTY_DETAILS} element={<PropertyDetails />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.PROPERTIES.CREATE} />}>
            <Route path={routeNames.ADD_PROPERTY} element={<AddEditProperties />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.PROPERTIES.UPDATE} />}>
            <Route path={routeNames.EDIT_PROPERTY} element={<AddEditProperties />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.COMPANIES.VIEW} />}>
            <Route path={routeNames.COMPANIES} element={<DisplayCompanies />} />
            <Route path={routeNames.COMPANY_DETAILS} element={<CompanyDetails />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.COMPANIES.CREATE} />}>
            <Route path={routeNames.ADD_COMPANY} element={<AddEditCompanies />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.COMPANIES.UPDATE} />}>
            <Route path={routeNames.EDIT_COMPANY} element={<AddEditCompanies />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}

function AppRoutes() {
  return (
    <Router>
      <RouteContent />
    </Router>
  );
}

export default AppRoutes;
